<template>
  <v-dialog v-model="isOpen" width="50%" persistent>
    <template v-slot:activator="activatorProps">
      <slot name="activator" v-bind="activatorProps" />
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('post_proposal') }}</span>
      </v-card-title>
      <v-card-text class="scrollable-content">
        <CustomForm
          ref="form"
          v-model="proposal"
          model="proposal"
          :default-fields="defaultFields"
          :possible-fields="getPossibleFields()"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="close">{{ $t('Cancel') }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!isValid"
          :loading="loading"
          raised
          data-testid="submit-ris-proposal"
          @click="postProposal"
        >
          {{ $t('post_proposal') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import Committee from '@/store/models/committee'
import get from 'lodash/get'

const postProposalUrl = '/ris/post_proposal/{committee}'

export default {
  name: 'RisProposal',
  props: {
    committee: {
      type: Committee,
      required: true,
    },
    meetingId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      defaultFields: ['attachments', 'title', 'wording'],
      proposal: { attachments: [] },
      isOpen: false,
      loading: false,
      proposalTypes: [
        { text: this.$t('proposal_type_amendment_application'), value: 'amendment_proposal' },
        { text: this.$t('proposal_type_order_application'), value: 'order_proposal' },
        { text: this.$t('proposal_type_declaration'), value: 'declaration' },
      ],
      businessChoices: [],
    }
  },
  computed: {
    businessContextKey() {
      return this.meetingId ? `meeting:${this.meetingId}` : `committee:${this.committee.id}`
    },
    isValid() {
      // A title or business is required, as well as the description or a file.
      return (
        (this.proposal.title || this.proposal.business) &&
        (this.proposal.wording || this.$refs.form.hasAttachments) &&
        this.$refs.form?.valid
      )
    },
  },
  watch: {
    businessContextKey: {
      immediate: true,
      async handler(contextKey) {
        this.$set(this.proposal, 'business', null)
        this.businessChoices = []
        await this.fetchBusinesses(contextKey)
      },
    },
  },
  methods: {
    ...mapActions({
      reset: 'risproposal/reset',
    }),
    close() {
      this.isOpen = false
      this.$refs.form.cleanup()
      this.loading = false
    },
    async fetchBusinesses(contextKey = this.businessContextKey) {
      const url = this.meetingId
        ? createLink('businesses')
        : createLink('committees/{id}/businesses', { id: this.committee.id })
      const payload = this.meetingId ? { params: { meeting_id: this.meetingId } } : {}
      const { data } = await api.get(url, payload)
      if (contextKey !== this.businessContextKey) {
        return
      }
      const businesses = Array.isArray(data) ? data : data?.results || []
      this.businessChoices = businesses.map((business) => ({
        text: business.display_name,
        value: business.api_uid,
      }))
    },
    getProposalPayload() {
      const business = this.proposal.business
      const businessValue = business?.value ?? business
      const businessTitle =
        business?.text ?? this.businessChoices.find(({ value }) => value === businessValue)?.text ?? businessValue
      const title =
        typeof this.proposal.title === 'string' && this.proposal.title.trim() ? this.proposal.title : businessTitle

      return {
        ...this.proposal,
        business: businessValue,
        title,
      }
    },
    async postProposal() {
      if (!this.isValid) {
        return
      }

      this.loading = true
      try {
        // Post to RIS.
        const formData = this.$refs.form.getFormData(this.getProposalPayload())
        const response = await api.post(createLink(postProposalUrl, { committee: this.committee.id }), formData)

        // RIS creation returns 201. A WAF may intercept the request and still respond with 200.
        if (response.status !== 201) {
          const error = new Error(this.$t('ris_submission_unexpected_response'))
          error.type = 'WAFException'
          throw error
        }

        // If successful, reset localStorage and close overlay.
        this.$emit('posted', response)
        this.reset()
        this.close()
      } catch (error) {
        const nonFieldErrors = get(error, 'response.data.non_field_errors', []).join(' ')
        const messageDetail = get(error, 'response.data.detail', null)
        const messageText = get(error, 'response.data.text', null)
        const messageToDisplay =
          messageText || messageDetail || nonFieldErrors || error.message || this.$t('unknown_error')

        this.notifyError(this.$t('SubmitProposalCardSubmitError', { message: messageToDisplay }))
        this.loading = false
        throw error
      }
    },
    getPossibleFields() {
      return {
        title: {
          component: 'v-text-field',
          identifier: 'title',
          label: this.$t('Business_case_title'),
          required: true,
        },
        business: {
          component: 'v-combobox',
          identifier: 'business',
          items: this.businessChoices,
          label: this.$t('Business_case_business'),
          required: true,
        },
        proposal_type: {
          clearable: true,
          component: 'v-select',
          identifier: 'proposal_type',
          items: this.proposalTypes,
          label: this.$t('proposal_type'),
        },
        reference: {
          component: 'v-text-field',
          identifier: 'reference',
          label: this.$t('reference'),
        },
        wording: {
          component: 'v-textarea',
          identifier: 'wording',
          label: this.$t('Wording'),
        },
        reason: {
          component: 'v-textarea',
          identifier: 'reason',
          label: this.$t('reason'),
        },
        submit_as_faction: {
          component: 'v-checkbox',
          identifier: 'submit_as_faction',
          label: this.$t('submit_as_faction'),
        },
        attachments: {
          component: 'FileInput',
          identifier: 'attachments',
          label: this.$t('proposal_form_attachment_label'),
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload-select-button {
  position: relative;
  overflow: hidden;
  margin: 10px 0 10px 10px;
}
.file-upload-file-input {
  display: none;
}
.scrollable-content {
  height: 40vh;
  overflow-y: auto;
}
</style>
